import React, { useState, useRef, useEffect, useCallback } from 'react';
import Icons from './Icons';

export default function MediaTrimmer({ file, type, maxDuration, onSave, onCancel }) {
  const [duration, setDuration] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const [mediaUrl, setMediaUrl] = useState('');
  const [currentTime, setCurrent] = useState(0);
  const [dragging, setDragging] = useState(null); // 'start' | 'end' | null
  const mediaRef = useRef(null);
  const trackRef = useRef(null);

  useEffect(() => {
    if (file) {
      let url = '';
      let shouldRevoke = false;

      if (file instanceof Blob || file instanceof File) {
        url = URL.createObjectURL(file);
        shouldRevoke = true;
      } else if (file.fileUrl || file.url) {
        url = file.fileUrl || file.url;
      } else if (typeof file === 'string') {
        url = file;
      }

      if (!url) return;

      setMediaUrl(url);
      const el = document.createElement(type === 'audio' ? 'audio' : 'video');
      el.src = url;
      el.onloadedmetadata = () => {
        const d = el.duration || 0;
        setDuration(d);
        setStartTime(0);
        setEndTime(Math.min(d, maxDuration));
      };
      
      return () => {
        if (shouldRevoke) URL.revokeObjectURL(url);
      };
    }
  }, [file, type, maxDuration]);

  useEffect(() => {
    const el = mediaRef.current;
    if (!el) return;
    const onTime = () => setCurrent(el.currentTime);
    el.addEventListener('timeupdate', onTime);
    return () => el.removeEventListener('timeupdate', onTime);
  }, [mediaUrl]);

  const selectedDuration = endTime - startTime;
  const pctStart = duration > 0 ? (startTime / duration) * 100 : 0;
  const pctEnd = duration > 0 ? (endTime / duration) * 100 : 100;
  const pctCurrent = duration > 0 ? (currentTime / duration) * 100 : 0;

  const getTimeFromX = useCallback((clientX) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return 0;
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return pct * duration;
  }, [duration]);

  const handleMouseDown = (handle) => (e) => {
    e.preventDefault();
    setDragging(handle);
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const t = getTimeFromX(e.clientX);
      if (dragging === 'start') {
        const newStart = Math.max(0, Math.min(t, endTime - 1));
        // Enforce max duration: if gap > max, push end down
        if (endTime - newStart > maxDuration) {
          setStartTime(newStart);
          setEndTime(newStart + maxDuration);
        } else {
          setStartTime(newStart);
        }
        if (mediaRef.current) mediaRef.current.currentTime = newStart;
      } else {
        const newEnd = Math.min(duration, Math.max(t, startTime + 1));
        // Enforce max duration: if gap > max, push start up
        if (newEnd - startTime > maxDuration) {
          setEndTime(newEnd);
          setStartTime(newEnd - maxDuration);
        } else {
          setEndTime(newEnd);
        }
        if (mediaRef.current) mediaRef.current.currentTime = Math.max(startTime, newEnd - 1);
      }
    };
    const onUp = () => setDragging(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging, startTime, endTime, duration, maxDuration, getTimeFromX]);

  const fmt = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    const ms = Math.floor((s % 1) * 10);
    return `${m}:${String(sec).padStart(2, '0')}.${ms}`;
  };

  return (
    <div className="mp-overlay" style={{ zIndex: 100 }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--s1)', border: '1px solid var(--b2)', borderRadius: 16,
        maxWidth: 520, width: '95%', padding: 0, boxShadow: '0 32px 100px rgba(0,0,0,.7)',
        animation: 'moin .25s ease-out', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', borderBottom: '1px solid var(--b1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>✂️</span>
            <span style={{ fontWeight: 800, fontSize: 16 }}>Trim {type === 'video' ? 'Video' : 'Audio'}</span>
          </div>
          <button className="mc-btn" onClick={onCancel}><Icons.X sz={14} /></button>
        </div>

        {/* Media Preview */}
        <div style={{ background: '#000', padding: type === 'audio' ? '24px 16px' : '0 16px' }}>
          {type === 'video' ? (
            <video ref={mediaRef} src={mediaUrl} controls style={{ width: '100%', maxHeight: 220, display: 'block', borderRadius: 8 }} />
          ) : (
            <audio ref={mediaRef} src={mediaUrl} controls style={{ width: '100%', display: 'block' }} />
          )}
        </div>

        {/* Trimmer Controls */}
        <div style={{ padding: '18px 22px 22px' }}>
          {/* Duration Info */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14, fontSize: 12 }}>
            <div style={{ display: 'flex', gap: 16 }}>
              <span style={{ color: 'var(--t3)' }}>Total: <strong style={{ color: 'var(--t1)' }}>{fmt(duration)}</strong></span>
              <span style={{ color: selectedDuration > maxDuration ? '#f87171' : '#10b981' }}>
                Selected: <strong>{fmt(selectedDuration)}</strong> / {maxDuration}s max
              </span>
            </div>
          </div>

          {/* Custom Dual-Handle Track */}
          <div ref={trackRef} style={{
            position: 'relative', height: 48, marginBottom: 16, userSelect: 'none', cursor: 'default',
          }}>
            {/* Track background */}
            <div style={{
              position: 'absolute', top: 16, left: 0, right: 0, height: 16,
              background: 'var(--s3)', borderRadius: 4, overflow: 'hidden',
            }}>
              {/* Selected range highlight */}
              <div style={{
                position: 'absolute', top: 0, bottom: 0,
                left: `${pctStart}%`, width: `${pctEnd - pctStart}%`,
                background: 'linear-gradient(90deg, rgba(139,92,246,.35), rgba(6,182,212,.35))',
                borderLeft: '2px solid #8b5cf6', borderRight: '2px solid #06b6d4',
              }} />
              {/* Playhead */}
              <div style={{
                position: 'absolute', top: -4, bottom: -4, width: 2,
                left: `${pctCurrent}%`, background: '#fff', borderRadius: 1, zIndex: 3,
              }} />
            </div>

            {/* Start Handle */}
            <div
              onMouseDown={handleMouseDown('start')}
              style={{
                position: 'absolute', top: 8, left: `calc(${pctStart}% - 10px)`,
                width: 20, height: 32, borderRadius: 6,
                background: dragging === 'start' ? '#8b5cf6' : 'var(--s2)',
                border: `2px solid ${dragging === 'start' ? '#a78bfa' : '#8b5cf6'}`,
                cursor: 'ew-resize', zIndex: 4,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,.4)',
                transition: 'background .15s',
              }}
            >
              <div style={{ width: 2, height: 12, background: '#8b5cf6', borderRadius: 1 }} />
            </div>

            {/* End Handle */}
            <div
              onMouseDown={handleMouseDown('end')}
              style={{
                position: 'absolute', top: 8, left: `calc(${pctEnd}% - 10px)`,
                width: 20, height: 32, borderRadius: 6,
                background: dragging === 'end' ? '#06b6d4' : 'var(--s2)',
                border: `2px solid ${dragging === 'end' ? '#22d3ee' : '#06b6d4'}`,
                cursor: 'ew-resize', zIndex: 4,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,.4)',
                transition: 'background .15s',
              }}
            >
              <div style={{ width: 2, height: 12, background: '#06b6d4', borderRadius: 1 }} />
            </div>
          </div>

          {/* Time Labels */}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontFamily: 'monospace', color: 'var(--t3)', marginBottom: 18 }}>
            <span style={{ color: '#8b5cf6', fontWeight: 700 }}>Start: {fmt(startTime)}</span>
            <span style={{ color: '#06b6d4', fontWeight: 700 }}>End: {fmt(endTime)}</span>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onCancel} style={{
              flex: 1, padding: 12, borderRadius: 10, border: '1px solid var(--b2)',
              background: 'var(--s2)', color: 'var(--t1)', fontWeight: 600, cursor: 'pointer', fontSize: 13,
            }}>Cancel</button>
            <button onClick={() => onSave(startTime, endTime)} style={{
              flex: 1, padding: 12, borderRadius: 10, border: 'none',
              background: 'linear-gradient(135deg, #8b5cf6, #06b6d4)', color: '#fff',
              fontWeight: 700, cursor: 'pointer', fontSize: 13,
              boxShadow: '0 4px 16px rgba(139,92,246,.3)',
            }}>✂️ Apply Trim</button>
          </div>
        </div>
      </div>
    </div>
  );
}
